export interface FlowStep {
  label: string;
  icon: string;
}

export const ZONE_EARNINGS_META = {
  title: 'Zone-Based Driver Earnings',
  subtitle: 'Customer fare and driver payout are calculated from the operational zone — not from per-service commission toggles.',
};

/** End-to-end zone earnings flow */
export const ZONE_DRIVER_EARNINGS_FLOW: FlowStep[] = [
  { label: 'Customer books ride', icon: 'Person' },
  { label: 'Zone detected at pickup', icon: 'Map' },
  { label: 'Fare from zone matrix', icon: 'Payments' },
  { label: 'Ride completed', icon: 'CheckCircle' },
  { label: 'Zone commission % deducted', icon: 'Percent' },
  { label: 'Driver payout settled', icon: 'AccountBalanceWallet' },
];

export interface ZoneEarningsRow {
  component: string;
  source: string;
  example: string;
}

export const ZONE_EARNINGS_BREAKDOWN: ZoneEarningsRow[] = [
  {
    component: 'Customer fare',
    source: 'Zone fare matrix (base + km + min + surge)',
    example: 'BLR_CENTRAL bike-ride → ₹85',
  },
  {
    component: 'Platform commission',
    source: 'Commission % set on each zone',
    example: '15% of ₹85 = ₹12.75',
  },
  {
    component: 'Driver payout',
    source: 'Zone fare − zone commission',
    example: '₹85 − ₹12.75 = ₹72.25',
  },
  {
    component: 'Settlement',
    source: 'Settlements module after ride complete',
    example: 'Weekly batch or instant wallet',
  },
];

export const ZONE_SETUP_ACTIONS = [
  { step: 1, label: 'Create zones with polygon boundaries', path: '/zones' },
  { step: 2, label: 'Enable this sub-service in each zone', path: undefined as string | undefined },
  { step: 3, label: 'Set fare matrix (base, per km, per min, surge)', path: '/zones' },
  { step: 4, label: 'Set driver commission % on zone', path: '/zones' },
  { step: 5, label: 'Onboard drivers and assign zones', path: '/onboarding' },
  { step: 6, label: 'Monitor earnings & process settlements', path: '/settlements' },
];

export const FARE_FORMULA = 'Total fare = baseFare + (distance × perKm) + (duration × perMin) + surge − discounts';

export const PAYOUT_FORMULA = 'Driver payout = zone fare − (zone fare × zone commission %)';

/** Zone wizard — how platform takes cut from zone fare */
export type PartnerPayoutModel = 'commission' | 'subscription' | 'both';

/** Zone partner commission rate (%). */
export const ZONE_COMMISSION_RATE_MIN = 0;
export const ZONE_COMMISSION_RATE_MAX = 100;
export const ZONE_COMMISSION_RATE_DECIMALS = 2;

/** Zone wizard — distance fields (base distance, base km). */
export const ZONE_DISTANCE_DECIMALS = 2;
export const ZONE_DISTANCE_MIN = 0;
export const ZONE_DISTANCE_MAX = 999;

/** Zone wizard — fare / charge amount fields. */
export const ZONE_PRICE_DECIMALS = 2;
export const ZONE_PRICE_MIN = 0;
export const ZONE_PRICE_MAX = 99_999;

/** Zone surge rule multiplier (1.0 = no surge, 3.0 = max cap aligned with live surge engine). */
export const ZONE_SURGE_MULTIPLIER_MIN = 1;
export const ZONE_SURGE_MULTIPLIER_MAX = 3;
export const ZONE_SURGE_MULTIPLIER_DECIMALS = 2;

/** Rental package hour/km bundles (Add Rental Package drawer). */
export const RENTAL_PACKAGE_HOURS_MIN = 1;
/** Up to 30 days (1 calendar month) of rental hours. */
export const RENTAL_PACKAGE_HOURS_MAX = 24 * 30;
export const RENTAL_PACKAGE_KM_MIN = 1;
export const RENTAL_PACKAGE_KM_MAX = ZONE_DISTANCE_MAX;
export const RENTAL_PACKAGE_PRICE_MIN = 1;
export const RENTAL_PACKAGE_PRICE_MAX = 999_999;
export const RENTAL_PACKAGE_PRICE_MAX_DIGITS = 6;
export const RENTAL_PACKAGE_GRACE_MIN = 0;
export const RENTAL_PACKAGE_GRACE_MAX = 9_999;
export const RENTAL_PACKAGE_GRACE_MAX_DIGITS = 4;
export const RENTAL_PACKAGE_EXTRA_KM_MAX = 9_999;
export const RENTAL_PACKAGE_EXTRA_KM_MAX_DIGITS = 4;
export const RENTAL_PACKAGE_GRACE_DEFAULT_CAR = 15;
export const RENTAL_PACKAGE_GRACE_DEFAULT_BIKE = 10;

/** Canonical service slug for zone-wide transport rental packages (not bike/car split). */
export const TRANSPORT_RENTAL_SERVICE_SLUG = 'rental';

/** Outstation pricing — GST % on package fare. */
export const OUTSTATION_GST_PERCENT_MIN = 0;
export const OUTSTATION_GST_PERCENT_MAX = 100;
export const OUTSTATION_GST_PERCENT_DECIMALS = 2;
export const OUTSTATION_GST_PERCENT_DEFAULT = 5;

/** Outstation pricing — KM slab bounds (Min KM is mandatory when creating a slab). */
export const OUTSTATION_KM_SLAB_MIN = 1;
export const OUTSTATION_KM_SLAB_MAX = ZONE_DISTANCE_MAX;
/** Max KM = 0 means no upper limit (unlimited slab). */
export const OUTSTATION_KM_SLAB_UNLIMITED = 0;

/** Outstation package surge multiplier when surge is enabled. */
export const OUTSTATION_SURGE_MULTIPLIER_DEFAULT = 1.2;

/** Sample waiting minutes used in admin fare preview when wait/min rate is set. */
export const OUTSTATION_FARE_PREVIEW_WAITING_MINUTES = 15;

export function commissionRateUsesZonePercent(model: PartnerPayoutModel) {
  return model === 'commission' || model === 'both';
}

export interface PayoutModelInfo {
  key: PartnerPayoutModel;
  title: string;
  description: string;
}

export const PAYOUT_MODELS: PayoutModelInfo[] = [
  { key: 'commission', title: 'Commission (zone %)', description: 'Deduct commission % from each zone fare' },
  { key: 'subscription', title: 'Subscription', description: 'Driver pays zone plan — optional add-on' },
  { key: 'both', title: 'Both', description: 'Driver chooses at onboarding' },
];
